import React, { useState } from 'react';
import FeedbackPill from '../ui/FeedbackPill.jsx';
import { enumLabels } from '../../lib/enums.js';

async function deleteEntry(id) {
  const res = await fetch(`/feedback_entries/${id}`, {
    method:      'DELETE',
    credentials: 'same-origin',
    headers: {
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      'Accept':       'application/json'
    }
  });
  if (res.status === 401) { window.location = '/session/new'; return false; }
  if (res.status === 403) {
    window.alert("You don't have permission — only the author or a project manager can delete this entry.");
    return false;
  }
  if (!res.ok && res.status !== 204) throw new Error(`DELETE failed (${res.status})`);
  return true;
}

const FLAG_COLOR = { on_track: '#1F6B41', watch: '#B4791A', at_risk: '#9B1F26' };

export default function FeedbackEntryRow({ entry, onDeleted, onEditRequested }) {
  const [busy, setBusy] = useState(false);
  const CHANNEL_LABEL = enumLabels('feedback_channel');
  const FLAG_LABEL    = enumLabels('feedback_health_flag');
  const isInternal = entry.kind === 'internal';
  const isConcern  = !isInternal && entry.sentiment === 'concern';
  const dotColor   = isInternal
    ? (entry.health_flag ? FLAG_COLOR[entry.health_flag] : '#9A9A95')
    : (isConcern ? '#9B1F26' : '#1F6B41');

  // Resolve opens the edit modal pre-filled with status='resolved' so the user
  // can optionally add a resolution note before saving.
  function resolve() {
    onEditRequested({ ...entry, status: 'resolved' });
  }

  async function destroy() {
    if (!window.confirm('Delete this entry?')) return;
    setBusy(true);
    try {
      const ok = await deleteEntry(entry.id);
      if (ok) onDeleted(entry.id);
    } finally {
      setBusy(false);
    }
  }

  const kindTagStyle = {
    fontSize: 10.5, fontWeight: 500, padding: '1px 7px', borderRadius: 5,
    background: isInternal ? '#F0EFE9' : '#EAF1FB',
    color:      isInternal ? '#6B6B68' : '#1F5A99'
  };

  return (
    <li className="relative">
      <span style={{
        position: 'absolute', left: -22, top: 6, width: 10, height: 10,
        borderRadius: 99, background: dotColor, border: '2px solid #fff'
      }} />
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12px]" style={{ color: '#888', fontVariantNumeric: 'tabular-nums' }}>
          {new Date(entry.created_at).toLocaleDateString()}
        </span>
        <span style={kindTagStyle}>{isInternal ? 'Internal' : 'Client'}</span>
        <span className="text-[13px] font-medium" style={{ color: '#1A1A1A' }}>{entry.title}</span>

        {isInternal ? (
          entry.health_flag && (
            <span className="text-[11px] font-medium" style={{ color: FLAG_COLOR[entry.health_flag] }}>
              ● {FLAG_LABEL[entry.health_flag]}
            </span>
          )
        ) : (
          <>
            <FeedbackPill
              sentiment={entry.sentiment}
              severity={entry.severity || undefined}
              status={isConcern ? entry.status : undefined}
            />
            <span className="text-[11px]" style={{ color: '#9A9A95' }}>· {CHANNEL_LABEL[entry.channel]}</span>
          </>
        )}

        {entry.origin === 'pm_sync' && (
          <span className="text-[11px]" style={{ color: '#9A9A95' }}>
            · auto · PM sync{entry.verified === false ? ' · unverified' : ''}
          </span>
        )}

        <span className="ml-auto flex items-center gap-2 text-[11px]">
          {isConcern && entry.status !== 'resolved' && (
            <button onClick={resolve} disabled={busy} style={{ background: 'none', border: 'none', color: busy ? '#9A9A95' : '#1F6B41', cursor: busy ? 'not-allowed' : 'pointer' }}>
              Resolve
            </button>
          )}
          <button onClick={() => onEditRequested(entry)} disabled={busy} style={{ background: 'none', border: 'none', color: busy ? '#9A9A95' : '#3A3A38', cursor: busy ? 'not-allowed' : 'pointer' }}>
            Edit
          </button>
          <button onClick={destroy} disabled={busy} style={{ background: 'none', border: 'none', color: busy ? '#9A9A95' : '#9B1F26', cursor: busy ? 'not-allowed' : 'pointer' }}>
            Delete
          </button>
        </span>
      </div>
      {entry.body && (
        <p className="text-[13px] mt-1.5" style={{ color: '#3A3A38', lineHeight: 1.55 }}>{entry.body}</p>
      )}
      {entry.resolution && entry.resolution.length > 0 && (
        <p className="text-[12px] mt-1.5" style={{ color: '#6B6B68', fontStyle: 'italic' }}>
          Resolution: {entry.resolution}
        </p>
      )}
      {entry.last_edited_by && (
        <p className="text-[11px] mt-1" style={{ color: '#9A9A95' }}>
          Last edited by {entry.last_edited_by.name}
        </p>
      )}
    </li>
  );
}
